/**
 * Country suggestion bar.
 *
 * A slim bar above the header offers the Intercut shop for the country the
 * visitor browses from (by IP, via Shopify's /browsing_context_suggestions.json).
 * It never redirects on its own: EU geo-blocking rules require the customer's
 * consent, and bots or price comparison crawlers (Idealo) must stay on the URL
 * they requested.
 *
 * The bar is shown at most once per domain. Add ?geo=XX to any URL to force
 * a country for testing (ignores and keeps the stored choice).
 *
 * The same file lives in both themes: the CZ store serves cz/pl/se, the EUR
 * store the other domains. Markup comes from snippets/country-suggestion.liquid.
 */
(() => {
  if (customElements.get('country-suggestion')) return;

  // hreflang must match the <link rel="alternate"> tags Shopify renders, so
  // "go" can open the same (translated) page within the same store.
  const SHOPS = {
    SK: { host: 'intercut.sk', hreflang: 'sk', lang: 'sk' },
    AT: { host: 'intercut.at', hreflang: 'de-AT', lang: 'de' },
    DE: { host: 'intercut-shop.de', hreflang: 'de-DE', lang: 'de' },
    IT: { host: 'intercut.it', hreflang: 'it-IT', lang: 'it' },
    ES: { host: 'intercut.es', hreflang: 'es-ES', lang: 'es' },
    FR: { host: 'intercut.fr', hreflang: 'fr-FR', lang: 'fr' },
    NL: { host: 'intercut.nl', hreflang: 'nl-NL', lang: 'nl' },
    BE: { host: 'intercut.be', hreflang: 'nl-BE', lang: 'nl' },
    CZ: { host: 'intercut.cz', hreflang: 'cs', lang: 'cs' },
    PL: { host: 'intercut.pl', hreflang: 'pl-PL', lang: 'pl' },
    SE: { host: 'intercut-shop.se', hreflang: 'sv-SE', lang: 'sv' },
  };
  const SHOP_BE_FR = { host: 'intercut.be', path: '/fr', hreflang: 'fr-BE', lang: 'fr' };

  const STORAGE_KEY = 'intercut:country-suggestion';
  const BOT_PATTERN = /bot|crawl|spider|slurp|preview|lighthouse|headless|idealo/i;

  const STRINGS = {
    cs: { text: 'Máme obchod i pro vaši zemi.', go: 'Otevřít {shop}', close: 'Zavřít' },
    sk: { text: 'Máme obchod aj pre vašu krajinu.', go: 'Otvoriť {shop}', close: 'Zavrieť' },
    de: { text: 'Wir haben auch einen Shop für Ihr Land.', go: '{shop} öffnen', close: 'Schließen' },
    it: { text: 'Abbiamo un negozio anche per il tuo paese.', go: 'Apri {shop}', close: 'Chiudi' },
    es: { text: 'También tenemos una tienda para tu país.', go: 'Abrir {shop}', close: 'Cerrar' },
    fr: { text: 'Nous avons aussi une boutique pour votre pays.', go: 'Ouvrir {shop}', close: 'Fermer' },
    nl: { text: 'We hebben ook een webshop voor uw land.', go: '{shop} openen', close: 'Sluiten' },
    pl: { text: 'Mamy też sklep dla Twojego kraju.', go: 'Otwórz {shop}', close: 'Zamknij' },
    sv: { text: 'Vi har en butik för ditt land också.', go: 'Öppna {shop}', close: 'Stäng' },
    en: { text: 'We also have a store for your country.', go: 'Open {shop}', close: 'Close' },
  };

  const readStorage = () => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  };

  const writeStorage = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
      // Private mode or blocked storage: the popup may show again, nothing breaks.
    }
  };

  const detectCountry = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true', {
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data?.detected_values?.country?.handle || null;
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };

  class CountrySuggestion extends HTMLElement {
    static shopFor(country, browserLanguage = '') {
      if (country === 'BE' && /^fr/i.test(browserLanguage)) return SHOP_BE_FR;
      return SHOPS[country] || null;
    }

    static shouldSuggest(shop, hostname) {
      return Boolean(shop) && shop.host !== hostname.replace(/^www\./, '');
    }

    /**
     * Same page in the target shop when Shopify lists it as an alternate
     * (same store, translated URL), otherwise the target shop's homepage.
     * @param {object} shop - Entry from SHOPS.
     * @param {Array<{hreflang: string, href: string}>} alternates - hreflang links of the current page.
     */
    static targetUrl(shop, alternates) {
      const match = alternates.find((alt) => alt.hreflang.toLowerCase() === shop.hreflang.toLowerCase());
      return match ? match.href : `https://${shop.host}${shop.path || '/'}`;
    }

    static strings(lang) {
      return STRINGS[lang] || STRINGS.en;
    }

    static flagUrl(country) {
      return `https://cdn.shopify.com/static/images/flags/${country.toLowerCase()}.svg`;
    }

    connectedCallback() {
      // Low priority: never compete with the page's own loading work.
      const schedule = window.requestIdleCallback || ((callback) => setTimeout(callback, 1500));
      schedule(() => this.init());
    }

    async init() {
      const forced = new URLSearchParams(location.search).get('geo');

      if (!forced) {
        if (window.Shopify?.designMode || BOT_PATTERN.test(navigator.userAgent)) return;
        if (readStorage()) return;
      }

      const country = forced ? forced.toUpperCase() : await detectCountry();
      const shop = CountrySuggestion.shopFor(country, navigator.language);
      if (!CountrySuggestion.shouldSuggest(shop, location.hostname)) return;

      if (!forced) writeStorage('shown');
      this.render(country, shop);
    }

    render(country, shop) {
      const strings = CountrySuggestion.strings(shop.lang);
      const alternates = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]')).map((link) => ({
        hreflang: link.hreflang,
        href: link.href,
      }));
      const fragment = this.querySelector('template').content.cloneNode(true);

      const flag = fragment.querySelector('[data-flag]');
      flag.src = CountrySuggestion.flagUrl(country);
      try {
        flag.alt = new Intl.DisplayNames([shop.lang], { type: 'region' }).of(country);
      } catch (error) {
        flag.alt = country;
      }

      fragment.querySelector('[data-text="text"]').textContent = strings.text;

      const go = fragment.querySelector('[data-action="go"]');
      go.href = CountrySuggestion.targetUrl(shop, alternates);
      go.textContent = strings.go.replace('{shop}', `${shop.host}${shop.path || ''}`);

      const close = fragment.querySelector('[data-action="close"]');
      close.setAttribute('aria-label', strings.close);
      close.addEventListener('click', () => this.remove());

      this.appendChild(fragment);
    }
  }

  customElements.define('country-suggestion', CountrySuggestion);
})();
